import { css, keyframes } from "@emotion/react";
import React, { useCallback, useRef, useState } from "react";
import { resetButtonStyle } from "../Button";
import { useTheme } from "../theme";

const Tab = ({ children, ...rest }: React.ComponentPropsWithoutRef<"div">) => {
  const theme = useTheme();

  return (
    <div
      role="button"
      tabIndex={0}
      {...rest}
      css={css([
        theme.typography.body,
        {
          position: "relative",
          boxSizing: "border-box",
          color: "inherit",
          border: "1px solid transparent",
          margin: 0,
          display: "block",
          outline: 0,
          padding: 0,
          userSelect: "none",
          borderRadius: 0,
          verticalAlign: "middle",
          justifyContent: "center",
          textDecoration: "none",
          backgroundColor: "transparent",
          appearance: "none",
          WebkitTapHighlightColor: "transparent",
          textRendering: "auto",
          wordSpacing: "nomral",
          textIndent: "0px",
          textShadow: "none",
          flexShrink: 0,
          cursor: "pointer",
          whiteSpace: "nowrap",
          gridRow: "1",
        },
        css`
          border-radius: 8px 8px 0px 0px;
          transition: background-color ${theme.transitions.duration.shortest};
          &:focus-visible {
            border-color: ${theme.palette.intentions.primary.main};
          }
          &:hover {
            background-color: ${theme.palette.action.hover};
          }
          & > * {
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: ${theme.spacing * 5}px;
            padding-right: ${theme.spacing * 3}px;
            padding-left: ${theme.spacing * 3}px;
          }
          a {
            color: inherit;
            text-decoration: none;
          }
        `,
      ])}
    >
      {typeof children === "string" ? <div>{children}</div> : children}
    </div>
  );
};

export interface BaseTabUIProps<T> {
  variant?: "underline" | "buttons";

  /**
   * 表示されるスクリーンとタブの見出しを指定します。
   *
   * 非同期でスクリーンの数は増減するような、このpropが後から変更されるケースは想定していません。
   * もしそういったケースが発生したら、Issueを立ててください。
   *
   * @default []
   */
  tabs?: {
    title: React.ReactNode;
    screen: React.ReactNode;
    icon?: React.ReactNode;
    data?: T;
  }[];
  /**
   * Tabの描画に利用するコンポーネントを指定します。
   * 基本的にスタイルの指定は行わないでください。
   */
  renderTab?: (
    props: T & { children: React.ReactNode },
  ) => React.ReactElement | null;
  /**
   * タブをどのような位置に表示するかを指定します。
   * @default "start"
   */
  tabPosition?: "start" | "center" | "end" | "evenly";
  /**
   * タブの下に付く装飾を指定します。
   * @default "default"
   */
  underline?: "default" | "shadow";
  /**
   * 最初に表示するタブのインデックスを指定します。
   *
   * インデックスはtabs propに指定された配列のうち、現在表示されているスクリーンのインデックスです。
   * @default 0
   */
  defaultIndex?: number;
  /**
   * タブがクリックされるなどしてインデックスが更新された際に発火するメソッドを指定します。
   *
   * 例えば、これが返すインデックスをローカルストレージなどに保存しておいて、
   * defaultIndexに渡すような実装をしておくとルーターライブラリを使わず、
   * 再読み込み時に前回表示していたタブを表示することができます。
   *
   * ただしURLのシェアなどで他のユーザーと同じタブを見るといったことはルーターライブラリを使う必要があります。詳しい実装はサンプルとなるStoryが用意されているので見てください。
   */
  onChange?: (index: number) => void;
}

export interface TabUIProps<T> extends BaseTabUIProps<T> {
  children?: never;
}

export interface TabUIHasChildrenProps<T> extends Omit<
  BaseTabUIProps<T>,
  "tabs"
> {
  tabs?: {
    title: React.ReactNode;
    screen?: never;
    icon?: React.ReactNode;
    data?: T;
  }[];
  children: React.ReactNode;
}

const slideIn = keyframes`
  to {
    transform: translateX(0px);
  }
`;

const underlineHeight = 4;

interface TabUIUnderlineHeaderProps<T> {
  tabs: TabUIHasChildrenProps<T>["tabs"] | TabUIProps<T>["tabs"];
  underline: BaseTabUIProps<T>["underline"];
  tabPosition: BaseTabUIProps<T>["tabPosition"];
  onChange: BaseTabUIProps<T>["onChange"];
  currentIndex: number;
  renderTab?: BaseTabUIProps<T>["renderTab"];
}

const DefaultTabContent = (props: React.PropsWithChildren) => {
  return <>{props.children}</>;
};

export const TabUIUnderlineHeader = <T,>({
  tabs = [],
  underline,
  tabPosition,
  onChange,
  currentIndex,
  renderTab: TabContent = DefaultTabContent,
}: TabUIUnderlineHeaderProps<T>) => {
  const theme = useTheme();
  const prevCurrentIndex = useRef(currentIndex);

  const handleUpdateIndex = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const nextIndex = parseInt(event.currentTarget.dataset.index || "0", 10);
      prevCurrentIndex.current = currentIndex;
      onChange?.(nextIndex);
    },
    [currentIndex, onChange],
  );
  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <div
        css={[
          css`
            box-sizing: border-box;
            display: grid;
            grid-auto-flow: column;
            overflow-x: auto;
          `,
          {
            justifyContent:
              tabPosition === "start"
                ? "flex-start"
                : tabPosition === "end"
                  ? "flex-end"
                  : tabPosition === "evenly"
                    ? "initial"
                    : "center",
          },
          underline === "shadow"
            ? {
                boxShadow: theme.shadows[1],
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.paper,
              }
            : underline === "default"
              ? {
                  boxShadow: `0px -${underlineHeight}px 0px 0px ${theme.palette.gray[300]} inset`,
                }
              : undefined,
        ]}
      >
        {tabs.map(({ title, data, icon }, index) => {
          return (
            <Tab onClick={handleUpdateIndex} data-index={index} key={index}>
              {/*
              // @ts-expect-error @emotion/reactの変更により型安全でなくなった https://github.com/emotion-js/emotion/compare/%40emotion/react%4011.13.0...%40emotion/react%4011.13.3#diff-a3e371cc2c719626107c869dcf5a0fad82d9ea99d37c727c96b3baf3c016fffdL89-R91*/}
              <TabContent {...(data as T)}>
                <span
                  css={css`
                    display: flex;
                    gap: 8px;
                  `}
                >
                  {icon}
                  {title}
                </span>
              </TabContent>
            </Tab>
          );
        })}
        <div
          key={currentIndex}
          style={{
            gridRow: 2,
            gridColumn: currentIndex + 1,
            transform: `translateX(${
              prevCurrentIndex.current === currentIndex
                ? 0
                : prevCurrentIndex.current > currentIndex
                  ? 100
                  : -100
            }%)`,
          }}
          css={css`
            height: ${underlineHeight}px;
            background-color: ${theme.palette.intentions.primary.main};
            border-radius: 8px;
            animation: ${slideIn} ${theme.transitions.duration.standard}
              ${theme.transitions.easing.sharp} forwards;
          `}
        />
      </div>
    </div>
  );
};

export const TabUI = <T,>({
  tabs = [],
  variant = "underline",
  underline = "default",
  tabPosition = "start",
  defaultIndex = 0,
  onChange,
  children,
  renderTab,
}: TabUIProps<T> | TabUIHasChildrenProps<T>) => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const theme = useTheme();

  if (tabs === undefined || tabs.length === 0) {
    return null;
  }

  return (
    <div>
      {variant === "underline" ? (
        <TabUIUnderlineHeader
          tabs={tabs}
          currentIndex={currentIndex}
          underline={underline}
          tabPosition={tabPosition}
          onChange={(index) => {
            setCurrentIndex(index);
            onChange?.(index);
          }}
          renderTab={renderTab}
        />
      ) : (
        <div
          css={[
            css`
              display: grid;
              grid-auto-flow: column;
              gap: 8px;
              padding-bottom: 8px;
              overflow-x: auto;
              overflow-y: hidden;
            `,
            {
              justifyContent:
                tabPosition === "start"
                  ? "flex-start"
                  : tabPosition === "end"
                    ? "flex-end"
                    : tabPosition === "evenly"
                      ? "initial"
                      : "center",
            },
          ]}
        >
          {tabs.map(({ title }, index) => {
            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setCurrentIndex(index);
                  onChange?.(index);
                }}
                css={[
                  resetButtonStyle,
                  theme.typography.body,
                  currentIndex === index
                    ? css`
                        color: ${theme.palette.intentions.primary.contrastText};
                        background-color: ${theme.palette.intentions.primary
                          .main};
                      `
                    : css`
                        color: ${theme.palette.text.primary};
                        background-color: ${theme.palette.gray[100]};
                        transition: background-color
                          ${theme.transitions.duration.shortest};
                        &:hover {
                          background-color: ${theme.palette.gray[300]};
                        }
                      `,
                  css`
                    border-radius: 4px;
                    & > * {
                      padding: 6px 8px;
                    }
                  `,
                ]}
              >
                {typeof title === "string" ? <div>{title}</div> : title}
              </div>
            );
          })}
        </div>
      )}
      {children ?? tabs[currentIndex].screen}
    </div>
  );
};
